import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getValidToken, API_URL } from './utils/authUtils';
import AdminRouteGuard from './components/AdminRouteGuard';
import { ADMIN_SECTIONS, clearPermissionsCache } from './utils/adminPermissions';
import { MobileSidebar } from './components/ResponsiveComponents';

const BACKEND_URL = API_URL;

const AdminPendingPosts = () => {
  const navigate = useNavigate();
  const [pendingPosts, setPendingPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingPost, setRejectingPost] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchPendingPosts();
  }, []);

  const fetchPendingPosts = async () => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/curated-posts/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setPendingPosts(response.data.posts || []);
    } catch (err) {
      console.error('Error fetching pending posts:', err);
      setError('Failed to load pending posts');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (postId) => {
    if (!window.confirm('Approve this post for the alumni network?')) return;

    try {
      setProcessingId(postId);
      setError('');
      const token = getValidToken();

      await axios.put(
        `${BACKEND_URL}/api/admin/curated-posts/${postId}/approve`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      // Remove from list
      setPendingPosts(prev => prev.filter(post => post.id !== postId));
      alert('Post approved successfully!');
    } catch (err) {
      console.error('Error approving post:', err);
      setError(err.response?.data?.detail || 'Failed to approve post');
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectModal = (post) => {
    setRejectingPost(post);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectingPost) return;

    try {
      setProcessingId(rejectingPost.id);
      setError('');
      const token = getValidToken();

      const url = rejectionReason
        ? `${BACKEND_URL}/api/admin/curated-posts/${rejectingPost.id}/reject?reason=${encodeURIComponent(rejectionReason)}`
        : `${BACKEND_URL}/api/admin/curated-posts/${rejectingPost.id}/reject`;

      await axios.put(url, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Remove from list
      setPendingPosts(prev => prev.filter(post => post.id !== rejectingPost.id));
      setShowRejectModal(false);
      setRejectingPost(null);
      setRejectionReason('');
      alert('Post rejected successfully!');
    } catch (err) {
      console.error('Error rejecting post:', err);
      setError(err.response?.data?.detail || 'Failed to reject post');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const renderSourceIcon = (sourceType) => {
    if (sourceType === 'google_news') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
          📰 Google News
        </span>
      );
    } else if (sourceType === 'youtube') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
          📺 YouTube
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
        🔗 Link
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AdminRouteGuard requiredPermission={ADMIN_SECTIONS.CONTENT_CURATION}>
      {/* Mobile Sidebar */}
      <MobileSidebar
        activeTab="pending-posts"
        onTabChange={(tabId) => {
          if (tabId === 'home') {
            navigate('/admin');
          } else if (tabId === 'profile') {
            localStorage.removeItem('token');
            localStorage.removeItem('authToken');
            clearPermissionsCache();
            navigate('/');
          }
        }}
        navigate={navigate}
        isOpen={isMobileSidebarOpen}
        onToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col gap-4">
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Pending Posts Approval</h1>
              <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
                Review and approve curator-shared content before it appears on the alumni network
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
              <button
                onClick={fetchPendingPosts}
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
              <button
                onClick={() => navigate('/admin')}
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Back to Admin
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('authToken');
                  clearPermissionsCache();
                  navigate('/');
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Pending Posts Count */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium text-blue-900">
                {pendingPosts.length} {pendingPosts.length === 1 ? 'post' : 'posts'} awaiting review
              </span>
            </div>
          </div>
        </div>

        {/* Posts List */}
        <div className="space-y-4">
          {pendingPosts.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">No pending posts</h3>
              <p className="mt-1 text-sm text-gray-500">
                All curator submissions have been reviewed. Check back later for new content.
              </p>
            </div>
          ) : (
            pendingPosts.map((post) => (
              <div key={post.id} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-6">
                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* Thumbnail */}
                    {post.source_thumbnail && (
                      <div className="flex-shrink-0">
                        <img
                          src={post.source_thumbnail}
                          alt={post.source_title}
                          className="w-full sm:w-32 h-32 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1">
                          {renderSourceIcon(post.source_type)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDate(post.created_at)}
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {post.source_title}
                      </h3>

                      {/* Curator Info */}
                      <div className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Curator:</span> {post.curator?.first_name} {post.curator?.last_name}
                        <span className="mx-2">•</span>
                        <span className="text-gray-500">{post.curator?.email}</span>
                      </div>

                      {/* Commentary */}
                      {post.post_content && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-xs font-medium text-gray-500 mb-1">Curator's Commentary:</div>
                          <p className="text-sm text-gray-700">{post.post_content}</p>
                        </div>
                      )}

                      {/* Source URL */}
                      <div className="mt-3">
                        <a
                          href={post.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          View original source
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col sm:flex-row gap-2 sm:justify-end">
                    <button
                      onClick={() => openRejectModal(post)}
                      disabled={processingId === post.id}
                      className="w-full sm:w-auto px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {processingId === post.id ? 'Processing...' : 'Reject'}
                    </button>
                    <button
                      onClick={() => handleApprove(post.id)}
                      disabled={processingId === post.id}
                      className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {processingId === post.id ? 'Processing...' : 'Approve & Publish'}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Reject Post
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Optionally provide a reason for rejecting this post. The curator will be notified.
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Reason for rejection (optional)"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="mt-4 flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectingPost(null);
                  setRejectionReason('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processingId !== null}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingId !== null ? 'Processing...' : 'Reject Post'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminRouteGuard>
  );
};

export default AdminPendingPosts;
